import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, unref as _unref, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-y-4 text-left",
  "data-testid": "send-record-step-3"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-bold text-textPrimary mb-1" }, "Tipo do registro *", -1)
const _hoisted_3 = { class: "flex flex-col gap-1" }
const _hoisted_4 = ["data-testid"]
const _hoisted_5 = ["value", "checked", "disabled", "data-testid", "onChange"]
const _hoisted_6 = { class: "flex-1" }
const _hoisted_7 = {
  key: 0,
  class: "text-xs text-neutral50"
}
const _hoisted_8 = {
  key: 0,
  class: "text-xs"
}
const _hoisted_9 = { key: 0 }
const _hoisted_10 = {
  class: "text-sm font-bold text-textPrimary",
  for: "send-record-description"
}
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "text-xs text-neutral50" }
const _hoisted_13 = { class: "text-sm font-bold text-textPrimary mb-1" }
const _hoisted_14 = ["onDrop"]
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-textPrimary" }, "Arraste os anexos aqui ou", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("label", {
  for: "send-record-file-input",
  class: "mt-2 rounded-md border-2 border-primary30 px-4 py-1 text-sm font-bold text-primary30 cursor-pointer",
  "data-testid": "send-record-file-select"
}, " Selecionar arquivos ", -1)
const _hoisted_17 = {
  id: "send-record-dropzone-hint",
  class: "text-xs text-primary30 mt-1"
}
const _hoisted_18 = {
  key: 0,
  class: "mt-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800",
  role: "alert",
  "data-testid": "send-record-file-errors"
}
const _hoisted_19 = { class: "mt-2 flex flex-col gap-1" }
const _hoisted_20 = { class: "flex flex-col" }
const _hoisted_21 = { class: "text-sm text-textPrimary truncate max-w-[240px]" }
const _hoisted_22 = { class: "text-xs text-neutral50" }
const _hoisted_23 = { class: "flex items-center gap-2" }
const _hoisted_24 = ["data-testid", "onClick"]
const _hoisted_25 = ["data-testid", "onClick"]
const _hoisted_26 = {
  key: 1,
  class: "text-xs text-neutral50 mt-1"
}
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-bold text-textPrimary mb-1" }, "Antes de enviar, confira os anexos", -1)
const _hoisted_28 = { class: "flex flex-col gap-1 rounded-lg border border-gray-200 px-3 py-2" }
const _hoisted_29 = ["data-testid"]
const _hoisted_30 = ["checked", "data-testid", "onChange"]
const _hoisted_31 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs text-neutral50 mt-1" }, " Nenhum item é obrigatório. A conferência é sua, aqui: ela não é enviada junto com o registro — quem audita vê os anexos. ", -1)

import { computed, ref } from 'vue'
import { useToast } from 'vue-toastification'
import {
  ActivityType,
  ACTIVITY_TYPE_LABELS,
  ACTIVITY_ATTACHMENT_LIMITS,
  ACTIVITY_DESCRIPTION_MIN_LENGTH
} from '@/constants/ds-attendance.constants'
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import AttachmentPanel from './AttachmentPanel.vue'
import {
  buildActivitySelfCheckItems,
  type ActivitySelfCheckKey,
  type ActivitySelfCheckState
} from './activity-self-check'
import {
  formatFileSize,
  requiresAttachment,
  requiresDescriptionOnTypeStep,
  validateAttachments
} from './types'

/**
 * Passo 3 de M2 — tipo do catálogo, descrição, anexos e checklist.
 *
 * "Ajuste administrativo" NÃO entra na lista do escalista (balão ⑭): ele é
 * exclusivo do perfil de backoffice/auditoria e não depende do catálogo da
 * escala — quem decide é a permissão, não a política.
 */

const ACTIVITY_TYPE_RADIO_GROUP = 'send-record-activity-type'


export default /*#__PURE__*/_defineComponent({
  __name: 'StepTypeAndFiles',
  props: {
    activityType: {},
    description: {},
    files: {},
    fileErrors: {},
    selfCheck: {},
    acceptedActivityTypes: {},
    showAdminAdjustment: { type: Boolean },
    formattedDate: {},
    stationCount: {},
    activeIndex: {}
  },
  emits: ["update:activityType", "update:description", "update:selfCheck", "update:fileErrors", "add-files", "remove-file", "update:activeIndex"],
  setup(__props: any, { emit }) {

const props = __props;





/** `name` compartilhado pelas opções: é ele que torna a escolha única. */
const toast = useToast()
const fileInput = ref<HTMLInputElement | null>(null)

/** O painel do anexo é o mesmo das etapas 4 e 5: coluna no desktop, faixa no celular. */
const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY)

const maxFiles = ACTIVITY_ATTACHMENT_LIMITS.maxFiles
const minLength = ACTIVITY_DESCRIPTION_MIN_LENGTH

/**
 * Remover um arquivo ANTES do que está na tela desloca todos os índices seguintes:
 * sem este ajuste o painel passaria a mostrar o vizinho. O índice novo vai ANTES da
 * remoção para o modal aplicar os dois na mesma rodada.
 */
function removeFile(index: number): void {
  const current = props.activeIndex ?? 0
  if (index < current) emit('update:activeIndex', current - 1)
  emit('remove-file', index)
}

const typeOptions = computed(() => {
  const accepted = props.acceptedActivityTypes
  const knowsPolicy = accepted.length > 0

  const catalog = Object.values(ActivityType).filter(
    (type) => type !== ActivityType.ADMIN_ADJUSTMENT
  )

  const options = catalog.map((type) => ({
    value: type,
    label: ACTIVITY_TYPE_LABELS[type],
    disabled: knowsPolicy && !accepted.includes(type),
    reason: 'Não aceito nesta escala'
  }))

  // §2.6 — o Ajuste administrativo exige perfil de auditoria E política que o
  // aceite; as duas condições já vêm resolvidas em `showAdminAdjustment`.
  if (props.showAdminAdjustment && accepted.includes(ActivityType.ADMIN_ADJUSTMENT)) {
    options.push({
      value: ActivityType.ADMIN_ADJUSTMENT,
      label: ACTIVITY_TYPE_LABELS[ActivityType.ADMIN_ADJUSTMENT],
      // C7 — a variação compacta é de um plantão só.
      disabled: props.stationCount > 1,
      reason: 'Disponível para um plantão por vez'
    })
  }

  return options
})

const showDescription = computed(() => props.activityType !== ActivityType.ADMIN_ADJUSTMENT)
const isDescriptionRequired = computed(() => requiresDescriptionOnTypeStep(props.activityType))
const attachmentRequired = computed(() => requiresAttachment(props.activityType))

const selfCheckItems = computed(() =>
  buildActivitySelfCheckItems({ formattedDate: props.formattedDate })
)

function toggleSelfCheck(key: ActivitySelfCheckKey): void {
  emit('update:selfCheck', { ...props.selfCheck, [key]: !props.selfCheck[key] })
}

function triggerFileInput(): void {
  fileInput.value?.click()
}

/**
 * Clicar em qualquer ponto da área ainda abre o seletor — menos quando o clique
 * já foi no `<label>`/`<input>`, que o abrem sozinhos: reabrir daqui mostraria
 * o diálogo duas vezes.
 */
function handleZoneClick(event: MouseEvent): void {
  const target = event.target as HTMLElement | null
  if (target?.closest('label, input')) return
  triggerFileInput()
}

function handleChange(event: Event): void {
  const target = event.target as HTMLInputElement
  void processIncoming(Array.from(target.files ?? []))
}

function handleDrop(event: DragEvent): void {
  void processIncoming(Array.from(event.dataTransfer?.files ?? []))
}

/**
 * Valida ANTES de materializar (a ordem do `StepFile.vue`: um arquivo de 62 MB
 * não precisa ir para a memória para ser recusado) e materializa só o que
 * passou. O que já estava anexado nunca é perdido — erro A de M2.
 *
 * A materialização com `arrayBuffer()` é obrigatória: em navegadores mobile o
 * `File` original é uma referência "lazy" (CamScanner/Files) que pode ficar
 * inacessível na hora do upload. NÃO remover — regressão conhecida (#1430
 * adicionou, #1751 removeu por engano, e voltou).
 */
async function processIncoming(incoming: File[]): Promise<void> {
  if (!incoming.length) return

  const { accepted, errors } = validateAttachments(incoming, props.files)
  emit('update:fileErrors', errors)

  const materialized: File[] = []
  for (const file of accepted) {
    try {
      const buffer = await file.arrayBuffer()
      materialized.push(
        new File([buffer], file.name, { type: file.type, lastModified: file.lastModified })
      )
    } catch (error) {
      console.error('[send-record] falha ao materializar anexo:', error)
      // eslint-disable-next-line no-restricted-syntax -- falha local ao ler o anexo, não erro de API
      toast.error(`Não foi possível carregar "${file.name}". Tente novamente.`)
    }
  }

  if (fileInput.value) fileInput.value.value = ''
  if (materialized.length) emit('add-files', materialized)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      _hoisted_2,
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(typeOptions.value, (option) => {
          return (_openBlock(), _createElementBlock("label", {
            key: option.value,
            class: _normalizeClass(["flex items-center gap-2 rounded border px-3 py-2 text-left text-sm", 
            _ctx.activityType === option.value
              ? 'border-primary bg-primary5/30 text-primary30 font-bold'
              : option.disabled
                ? 'border-gray-200 text-neutralLight cursor-not-allowed'
                : 'border-gray-200 text-textPrimary cursor-pointer'
          ]),
            "data-testid": `send-record-type-${option.value}`
          }, [
            _createElementVNode("input", {
              type: "radio",
              name: ACTIVITY_TYPE_RADIO_GROUP,
              class: _normalizeClass(option.disabled ? 'cursor-not-allowed' : 'accent-primary'),
              value: option.value,
              checked: _ctx.activityType === option.value,
              disabled: option.disabled || undefined,
              "data-testid": `send-record-type-${option.value}-input`,
              onChange: ($event: any) => (emit('update:activityType', option.value))
            }, null, 42, _hoisted_5),
            _createElementVNode("span", _hoisted_6, [
              _createTextVNode(_toDisplayString(option.label) + " ", 1),
              (option.value === _unref(ActivityType).OTHER)
                ? (_openBlock(), _createElementBlock("span", _hoisted_7, " — exige descrição "))
                : _createCommentVNode("", true)
            ]),
            (option.disabled)
              ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(option.reason), 1))
              : _createCommentVNode("", true)
          ], 10, _hoisted_4))
        }), 128))
      ])
    ]),
    (showDescription.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("label", _hoisted_10, " Descrição " + _toDisplayString(isDescriptionRequired.value ? '*' : '(opcional)'), 1),
          _createElementVNode("textarea", {
            id: "send-record-description",
            "data-testid": "send-record-description",
            rows: "3",
            maxlength: "2000",
            class: "w-full rounded border border-solid border-gray-200 px-2 py-1 text-sm",
            value: _ctx.description,
            onInput: _cache[0] || (_cache[0] = ($event: any) => (emit('update:description', ($event.target as HTMLTextAreaElement).value)))
          }, null, 40, _hoisted_11),
          _createElementVNode("p", _hoisted_12, " Obrigatória quando o tipo é \"Outros\" (mínimo " + _toDisplayString(_unref(minLength)) + " caracteres). " + _toDisplayString(_ctx.description.trim().length) + "/2000 ", 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", null, [
      _createElementVNode("p", _hoisted_13, " Anexos " + _toDisplayString(attachmentRequired.value ? '*' : '(opcional)'), 1),
      _createElementVNode("div", {
        "data-testid": "send-record-dropzone",
        class: "border-2 border-dashed border-primary30 rounded-2xl bg-primary5/30 p-6 flex flex-col items-center justify-center cursor-pointer",
        onClick: handleZoneClick,
        onDragover: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
        onDrop: _withModifiers(handleDrop, ["prevent"])
      }, [
        _createElementVNode("input", {
          id: "send-record-file-input",
          ref_key: "fileInput",
          ref: fileInput,
          type: "file",
          class: "sr-only",
          multiple: "",
          accept: ".jpeg,.jpg,.png,.pdf",
          "aria-describedby": "send-record-dropzone-hint",
          "data-testid": "send-record-file-input",
          onChange: handleChange
        }, null, 544),
        _hoisted_15,
        _hoisted_16,
        _createElementVNode("p", _hoisted_17, " JPG, PNG ou PDF · até 50 MB por arquivo · 1 a " + _toDisplayString(_unref(maxFiles)) + " arquivos ", 1)
      ], 40, _hoisted_14),
      (_ctx.fileErrors.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_18, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.fileErrors, (error) => {
              return (_openBlock(), _createElementBlock("li", { key: error }, _toDisplayString(error), 1))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_19, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.files, (file, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: `${file.name}-${index}`,
            class: "flex items-center justify-between rounded border border-gray-200 px-3 py-2",
            "data-testid": "send-record-file-item"
          }, [
            _createElementVNode("span", _hoisted_20, [
              _createElementVNode("span", _hoisted_21, _toDisplayString(file.name), 1),
              _createElementVNode("span", _hoisted_22, _toDisplayString(_unref(formatFileSize)(file.size)), 1)
            ]),
            _createElementVNode("span", _hoisted_23, [
              _createElementVNode("button", {
                type: "button",
                class: "text-xs text-primary30 underline",
                "data-testid": `send-record-file-preview-${index}`,
                onClick: ($event: any) => (emit('update:activeIndex', index))
              }, " Mostrar no painel ", 8, _hoisted_24),
              _createElementVNode("button", {
                type: "button",
                class: "text-xs text-red-700 underline",
                "data-testid": `send-record-file-remove-${index}`,
                onClick: ($event: any) => (removeFile(index))
              }, " Remover ", 8, _hoisted_25)
            ])
          ]))
        }), 128))
      ]),
      (_ctx.files.length && _ctx.stationCount > 1)
        ? (_openBlock(), _createElementBlock("p", _hoisted_26, " Os " + _toDisplayString(_ctx.files.length) + " anexos valem para os " + _toDisplayString(_ctx.stationCount) + " plantões selecionados. ", 1))
        : _createCommentVNode("", true)
    ]),
    (_ctx.files.length)
      ? (_openBlock(), _createBlock(AttachmentPanel, {
          key: 1,
          files: _ctx.files,
          layout: _unref(isDesktop) ? 'side' : 'carousel',
          "active-index": _ctx.activeIndex,
          "onUpdate:activeIndex": _cache[2] || (_cache[2] = ($event: any) => (emit('update:activeIndex', $event)))
        }, null, 8, ["files", "layout", "active-index"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", null, [
      _hoisted_27,
      _createElementVNode("div", _hoisted_28, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selfCheckItems.value, (item) => {
          return (_openBlock(), _createElementBlock("label", {
            key: item.key,
            class: "flex items-start gap-2 text-sm text-textPrimary cursor-pointer",
            "data-testid": `send-record-self-check-${item.key}`
          }, [
            _createElementVNode("input", {
              type: "checkbox",
              class: "mt-0.5",
              checked: _ctx.selfCheck[item.key],
              "data-testid": `send-record-self-check-checkbox-${item.key}`,
              onChange: ($event: any) => (toggleSelfCheck(item.key))
            }, null, 40, _hoisted_30),
            _createElementVNode("span", null, _toDisplayString(item.label), 1)
          ], 8, _hoisted_29))
        }), 128))
      ]),
      _hoisted_31
    ])
  ]))
}
}

})